/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Coins, Users, RefreshCcw } from "lucide-react";

export default function AdminShopPanel() {
  const [items, setItems] = useState(null);
  const [drafts, setDrafts] = useState({}); // {item_id: {price, unlock_trainers}}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/shop");
      setItems(data.items);
      const next = {};
      data.items.forEach((it) => {
        next[it.item_id] = {
          price: it.price,
          unlock_trainers: it.unlock_trainers,
        };
      });
      setDrafts(next);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Accès refusé");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!items) {
    return <div className="font-mono text-xs text-[#7A7A7A]">Chargement de la boutique…</div>;
  }

  const update = (item_id, field, value) => {
    setDrafts((d) => ({
      ...d,
      [item_id]: {
        ...d[item_id],
        [field]: value === "" ? "" : Math.max(0, parseInt(value) || 0),
      },
    }));
  };

  const isDirty = (it) => {
    const d = drafts[it.item_id];
    if (!d) return false;
    return d.price !== it.price || d.unlock_trainers !== it.unlock_trainers;
  };

  const save = async (it) => {
    const d = drafts[it.item_id];
    if (!d || d.price === "" || d.unlock_trainers === "") {
      toast.error("Renseigne un prix et un nombre de dresseurs.");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/shop/${it.item_id}`, {
        price: d.price,
        unlock_trainers: d.unlock_trainers,
      });
      toast.success(`${it.name} mis à jour.`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resetItem = async (it) => {
    setBusy(true);
    try {
      await api.delete(`/admin/shop/${it.item_id}/override`);
      toast.success(`${it.name} réinitialisé.`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-shop-panel">
      <div className="mb-3 panel p-3 bg-[#FCF9F2] flex items-start gap-3">
        <p className="text-[11px] font-mono text-[#4A4A4A] flex-1">
          Ajuste le prix et le nombre minimum de dresseurs requis pour débloquer chaque article.
          <br/>
          <strong>Important</strong> : modifie la valeur puis clique sur <em>Sauvegarder</em> (bouton vert clignotant).
          Les changements sont visibles dans la Boutique des joueurs dans les 5 s qui suivent.
        </p>
        <button
          data-testid="admin-shop-refresh"
          onClick={load}
          className="btn-ink text-[10px] py-1 px-2 shrink-0"
        >
          <RefreshCcw size={10}/> Recharger
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-shop-grid">
        {items.map((it) => {
          const d = drafts[it.item_id] || {};
          const dirty = isDirty(it);
          return (
            <div
              key={it.item_id}
              data-testid={`admin-shop-${it.item_id}`}
              className={`panel p-3 flex items-start gap-3 ${it.is_overridden ? "border-[#B87333]" : ""}`}
            >
              <img src={it.sprite} alt={it.name} className="pixelated w-12 h-12 shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="font-serif-display text-lg leading-tight truncate">{it.name}</h3>
                  {it.is_overridden && (
                    <span className="font-mono text-[9px] text-[#B87333] uppercase">● modifié</span>
                  )}
                </div>
                <p className="text-[10px] text-[#4A4A4A] mt-0.5 leading-tight">{it.description}</p>

                <div className="grid grid-cols-2 gap-2 mt-2">
                  <label className="block">
                    <span className="overline text-[9px] flex items-center gap-1">
                      <Coins size={9}/> Prix (₽)
                    </span>
                    <input
                      type="number"
                      min="1"
                      max="999999"
                      value={d.price ?? ""}
                      onChange={(e) => update(it.item_id, "price", e.target.value)}
                      data-testid={`admin-shop-price-${it.item_id}`}
                      className="w-full font-mono text-xs border border-[#2B2B2B] bg-[#FCF9F2] px-2 py-1 mt-0.5"
                    />
                    <span className="font-mono text-[9px] text-[#7A7A7A]">défaut {it.default_price}</span>
                  </label>
                  <label className="block">
                    <span className="overline text-[9px] flex items-center gap-1">
                      <Users size={9}/> Dresseurs requis
                    </span>
                    <input
                      type="number"
                      min="0"
                      max="12"
                      value={d.unlock_trainers ?? ""}
                      onChange={(e) => update(it.item_id, "unlock_trainers", e.target.value)}
                      data-testid={`admin-shop-unlock-${it.item_id}`}
                      className="w-full font-mono text-xs border border-[#2B2B2B] bg-[#FCF9F2] px-2 py-1 mt-0.5"
                    />
                    <span className="font-mono text-[9px] text-[#7A7A7A]">défaut {it.default_unlock_trainers}</span>
                  </label>
                </div>

                <div className="flex gap-1 mt-2 items-center">
                  <button
                    data-testid={`admin-shop-save-${it.item_id}`}
                    onClick={() => save(it)}
                    disabled={busy || !dirty}
                    className={`btn-ink text-[10px] py-1 px-2 ${dirty ? "btn-leaf ring-2 ring-[#B87333] animate-pulse" : "opacity-50"}`}
                  >
                    <Save size={10}/> {dirty ? "Sauvegarder (non sauvé !)" : "Sauvegardé"}
                  </button>
                  {it.is_overridden && (
                    <button
                      data-testid={`admin-shop-reset-${it.item_id}`}
                      onClick={() => resetItem(it)}
                      disabled={busy}
                      className="btn-ink text-[10px] py-1 px-2"
                    >
                      <RotateCcw size={10}/> Défaut
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
